import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { obtenerMisHorasMesActual, obtenerMisHorasPorClienteMes, obtenerMisHorasPorSemana, obtenerMisHorasPorFecha, obtenerMisHorasResumen, obtenerMisHorasClientePorFecha, obtenerMisHorasClientePorSemana, logoutUsuario, getTodosClientes } from '../../actions/index.js';
import NavBarHome from '../NavBarHome/NavBarHome.jsx';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner.jsx';
import Select from 'react-select';
import { utils, write } from 'xlsx';
import { saveAs } from 'file-saver';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, LabelList
} from 'recharts';
import './MisHoras.css';

// Paleta de colores para los gráficos (verde corporativo + variantes)
const CHART_COLORS = ['#28C76F', '#3FE07F', '#1E9E75', '#6CECA0', '#8A988F'];

const tooltipStyle = {
  background: '#0C120F',
  border: '0.5px solid rgba(40,199,111,0.3)',
  borderRadius: 10,
  color: '#E8EFEA',
  fontSize: 13,
};

const MisHoras = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  
  // Selectors de Redux
  const user = useSelector(state => state.usuario);
  const misHoras = useSelector(state => state.misHoras);
  const clientes = useSelector(state => state.clientesReporte);
  
  // Estados locales para filtros
  const [filtroActivo, setFiltroActivo] = useState('ninguno');
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [fechaEspecifica, setFechaEspecifica] = useState(null);
  const [fechaInicio, setFechaInicio] = useState(null);
  const [fechaFin, setFechaFin] = useState(null);
  const [mes, setMes] = useState('');
  const [año, setAño] = useState('');
  
  // Estados específicos para el filtro por cliente
  const [tipoFiltroCliente, setTipoFiltroCliente] = useState('todas'); // 'todas', 'mes-año', 'fecha-especifica', 'rango-fechas'
  const [clienteFechaEspecifica, setClienteFechaEspecifica] = useState(null);
  const [clienteFechaInicio, setClienteFechaInicio] = useState(null);
  const [clienteFechaFin, setClienteFechaFin] = useState(null);
  
  // Estado para el cliente actualmente filtrado (se actualiza al hacer búsqueda)
  const [clienteFiltrado, setClienteFiltrado] = useState(null);

  // useEffect para cargar datos iniciales
  useEffect(() => {
    dispatch(getTodosClientes());
  }, [dispatch]);

  // Función para manejar el cierre de sesión
  const handleLogout = () => {
    dispatch(logoutUsuario());
    history.push('/login');
  };

  // Función para formatear fecha para mostrar
  const formatearFecha = (fechaString) => {
    // fechaString viene en formato dd/mm/yyyy del backend
    const [dia, mes, año] = fechaString.split('/');
    return `${dia}/${mes}/${año}`;
  };

  // Función para formatear horas
  const formatearHoras = (horas) => {
    return parseFloat(horas).toFixed(2);
  };

  // Función para convertir cadena a buffer de bytes (necesaria para Excel)
  const s2ab = (s) => {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xFF;
    return buf;
  };

  // Función para exportar datos a Excel
  const exportarAExcel = () => {
    if (!misHoras.horas || misHoras.horas.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    try {
      // Crear un nuevo libro de trabajo
      const wb = utils.book_new();
      
      // Convertir los datos a formato para Excel
      const ws_data = misHoras.horas.map(hora => ([
        formatearFecha(hora.fechaCarga),
        hora.cliente?.nombre || 'Sin cliente',
        hora.servicio?.nombre || 'Sin servicio',
        hora.tarea?.nombre || 'Sin tarea',
        hora.horas,
        hora.descripcion || ''
      ]));

      // Añadir títulos a la primera fila
      ws_data.unshift(['Fecha', 'Cliente', 'Servicio', 'Tarea', 'Horas', 'Descripción']);

      // Crear hoja de trabajo
      const ws = utils.aoa_to_sheet(ws_data);
      
      // Añadir la hoja al libro
      utils.book_append_sheet(wb, ws, "Mis Horas");
      
      // Escribir y descargar el archivo
      const wbout = write(wb, { bookType: 'xlsx', type: 'binary' });
      const fileName = `mis_horas_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(new Blob([s2ab(wbout)], { type: 'application/octet-stream' }), fileName);
      
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      alert('Error al exportar el archivo');
    }
  };

  // Función para exportar resumen de clientes a Excel
  const exportarResumenClientesAExcel = () => {
    const resumenClientes = obtenerResumenClientes();
    if (!resumenClientes || resumenClientes.length === 0) {
      alert('No hay datos de resumen de clientes para exportar');
      return;
    }

    try {
      // Crear un nuevo libro de trabajo
      const wb = utils.book_new();
      
      // Convertir los datos del resumen a formato para Excel
      const ws_data = resumenClientes.map(cliente => ([
        cliente.nombre,
        cliente.horas,
        `${((cliente.horas / (misHoras.totalHoras || 1)) * 100).toFixed(1)}%`
      ]));

      // Añadir títulos a la primera fila
      ws_data.unshift(['Cliente', 'Horas Totales', '% del Total']);

      // Añadir fila de totales
      ws_data.push(['TOTAL', misHoras.totalHoras || 0, '100%']);

      // Crear hoja de trabajo
      const ws = utils.aoa_to_sheet(ws_data);
      
      // Añadir la hoja al libro
      utils.book_append_sheet(wb, ws, "Resumen por Cliente");
      
      // Escribir y descargar el archivo
      const wbout = write(wb, { bookType: 'xlsx', type: 'binary' });
      const fileName = `resumen_clientes_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(new Blob([s2ab(wbout)], { type: 'application/octet-stream' }), fileName);
      
    } catch (error) {
      console.error('Error al exportar resumen de clientes a Excel:', error);
      alert('Error al exportar el archivo');
    }
  };

  // Función para obtener resumen de clientes
  const obtenerResumenClientes = () => {
    if (!misHoras.horas || misHoras.horas.length === 0) return [];
    
    const clientesMap = new Map();
    
    misHoras.horas.forEach(hora => {
      const clienteNombre = hora.cliente?.nombre || 'Sin cliente';
      if (clientesMap.has(clienteNombre)) {
        clientesMap.set(clienteNombre, clientesMap.get(clienteNombre) + parseFloat(hora.horas));
      } else {
        clientesMap.set(clienteNombre, parseFloat(hora.horas));
      }
    });
    
    return Array.from(clientesMap.entries())
      .map(([nombre, horas]) => ({ nombre, horas }))
      .sort((a, b) => b.horas - a.horas);
  };

  // Función para obtener descripción del filtro aplicado
  const obtenerDescripcionFiltro = () => {
    switch (filtroActivo) {
      case 'por-cliente':
        if (tipoFiltroCliente === 'mes-año') {
          const mesTexto = mes ? `/${mes}` : '';
          const añoTexto = año || new Date().getFullYear();
          return `Cliente: ${clienteFiltrado?.label || 'N/A'} - ${añoTexto}${mesTexto}`;
        } else if (tipoFiltroCliente === 'rango-fechas') {
          const fechaInicio = clienteFechaInicio ? clienteFechaInicio.toLocaleDateString('es-ES') : 'N/A';
          const fechaFin = clienteFechaFin ? clienteFechaFin.toLocaleDateString('es-ES') : 'N/A';
          return `Cliente: ${clienteFiltrado?.label || 'N/A'} - Del ${fechaInicio} al ${fechaFin}`;
        } else {
          return `Cliente: ${clienteFiltrado?.label || 'N/A'} - Todas las fechas`;
        }
      case 'por-semana':
        const fechaInicioSem = fechaInicio ? fechaInicio.toLocaleDateString('es-ES') : 'N/A';
        const fechaFinSem = fechaFin ? fechaFin.toLocaleDateString('es-ES') : 'N/A';
        return `Rango de fechas: Del ${fechaInicioSem} al ${fechaFinSem}`;
      case 'por-fecha':
        const fechaEsp = fechaEspecifica ? fechaEspecifica.toLocaleDateString('es-ES') : 'N/A';
        return `Fecha específica: ${fechaEsp}`;
      default:
        return 'Todas las fechas';
    }
  };

  // Funciones para manejar filtros
  const handleFiltroPorCliente = () => {
    if (!clienteSeleccionado) {
      alert('Por favor seleccione un cliente');
      return;
    }
    setFiltroActivo('por-cliente');
    // Establecer el cliente filtrado cuando se ejecuta la búsqueda
    setClienteFiltrado(clienteSeleccionado);
    
    // Manejar diferentes tipos de filtro por cliente
    switch (tipoFiltroCliente) {
      case 'mes-año':
        // Filtro por mes y año específico
        const mesParam = mes.trim() === '' ? null : mes;
        const añoParam = año.trim() === '' ? null : año;
        dispatch(obtenerMisHorasPorClienteMes(clienteSeleccionado.value, mesParam, añoParam));
        break;
        
      case 'fecha-especifica':
        // Filtro por fecha específica con cliente
        if (!clienteFechaEspecifica) {
          alert('Por favor seleccione una fecha específica');
          return;
        }
        const fechaEspecificaStr = clienteFechaEspecifica.toISOString().split('T')[0];
        dispatch(obtenerMisHorasClientePorFecha(clienteSeleccionado.value, fechaEspecificaStr));
        break;
        
      case 'rango-fechas':
        // Filtro por rango de fechas (semana o período personalizado) con cliente
        if (!clienteFechaInicio || !clienteFechaFin) {
          alert('Por favor seleccione las fechas de inicio y fin');
          return;
        }
        const clienteFechaInicioStr = clienteFechaInicio.toISOString().split('T')[0];
        const clienteFechaFinStr = clienteFechaFin.toISOString().split('T')[0];
        dispatch(obtenerMisHorasClientePorSemana(clienteSeleccionado.value, clienteFechaInicioStr, clienteFechaFinStr));
        break;
        
      default:
        // Sin filtro de fecha - todas las horas del cliente
        dispatch(obtenerMisHorasPorClienteMes(clienteSeleccionado.value, null, null));
        break;
    }
  };

  const handleFiltroPorSemana = () => {
    if (!fechaInicio || !fechaFin) {
      alert('Por favor seleccione las fechas de inicio y fin de la semana');
      return;
    }
    setFiltroActivo('por-semana');
    const fechaInicioStr = fechaInicio.toISOString().split('T')[0];
    const fechaFinStr = fechaFin.toISOString().split('T')[0];
    dispatch(obtenerMisHorasPorSemana(fechaInicioStr, fechaFinStr));
  };

  const handleFiltroPorFecha = () => {
    if (!fechaEspecifica) {
      alert('Por favor seleccione una fecha');
      return;
    }
    setFiltroActivo('por-fecha');
    const fechaEspecificaStr = fechaEspecifica.toISOString().split('T')[0];
    dispatch(obtenerMisHorasPorFecha(fechaEspecificaStr));
  };

  // Función para limpiar todos los filtros
  const limpiarFiltros = () => {
    setFiltroActivo('ninguno');
    setClienteSeleccionado(null);
    setClienteFiltrado(null); // Limpiar el cliente filtrado
    setMes('');
    setAño('');
    setFechaEspecifica(null);
    setFechaInicio(null);
    setFechaFin(null);
    // Limpiar filtros específicos del cliente
    setTipoFiltroCliente('todas');
    setClienteFechaEspecifica(null);
    setClienteFechaInicio(null);
    setClienteFechaFin(null);
    
    // Limpiar solo los datos de misHoras para quitar las tablas
    dispatch({
      type: 'CLEAR_MIS_HORAS'
    });
  };

  // Preparar opciones para el select de clientes
  const opcionesClientes = clientes.map(cliente => ({
    value: cliente.id,
    label: cliente.nombre
  }));

  // Estilos personalizados para React Select (paleta moderna oscura + verde)
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: '#0A100D',
      borderColor: state.isFocused ? 'rgba(40,199,111,0.6)' : 'rgba(255,255,255,0.10)',
      borderWidth: '0.5px',
      borderRadius: '10px',
      color: '#E8EFEA',
      minHeight: '42px',
      height: '42px',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(40,199,111,0.20)' : 'none',
      '&:hover': { borderColor: 'rgba(40,199,111,0.45)' }
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#0C120F',
      border: '0.5px solid rgba(40,199,111,0.25)',
      borderRadius: '11px',
      overflow: 'hidden',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.6)',
      zIndex: 9999
    }),
    menuList: (provided) => ({ ...provided, padding: '6px', maxHeight: '230px' }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? 'rgba(40,199,111,0.16)' : state.isFocused ? 'rgba(40,199,111,0.12)' : 'transparent',
      color: state.isSelected || state.isFocused ? '#EAF6EF' : '#CBD6CF',
      borderRadius: '8px',
      padding: '9px 11px',
      cursor: 'pointer',
    }),
    singleValue: (provided) => ({ ...provided, color: '#E8EFEA', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
    placeholder: (provided) => ({ ...provided, color: '#9FB0A6', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
    input: (provided) => ({ ...provided, color: '#E8EFEA', fontSize: 13 }),
    valueContainer: (provided) => ({ ...provided, backgroundColor: 'transparent', flexWrap: 'nowrap', height: '42px', padding: '0 10px' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (provided, state) => ({ ...provided, color: state.isFocused ? '#3FE07F' : '#5C6862', '&:hover': { color: '#3FE07F' } }),
    clearIndicator: (provided) => ({ ...provided, color: '#9FB0A6', '&:hover': { color: '#E86D6D' } }),
  };

  // ===== Datos derivados para los gráficos =====
  const horasArr = misHoras.horas || [];
  const clientesUnicos = useMemo(() => new Set(horasArr.map(h => h.cliente?.nombre || 'Sin cliente')).size, [horasArr]);

  // Bar: horas por día (orden cronológico, últimos 20 días)
  const horasPorDia = useMemo(() => {
    const m = {};
    horasArr.forEach(h => {
      const f = h.fechaCarga;
      m[f] = (m[f] || 0) + parseFloat(h.horas || 0);
    });
    return Object.entries(m)
      .map(([f, horas]) => {
        const [d, mm, yyyy] = f.split('/');
        return { etiqueta: `${d}/${mm}`, orden: new Date(yyyy, mm - 1, d).getTime(), horas: Math.round(horas * 100) / 100 };
      })
      .sort((a, b) => a.orden - b.orden)
      .slice(-20);
  }, [horasArr]);

  // Dona: distribución por cliente (top 4 + otros)
  const donutClientes = useMemo(() => {
    const arr = obtenerResumenClientes().map(c => ({ nombre: c.nombre, horas: Math.round(c.horas * 100) / 100 }));
    if (arr.length <= 5) return arr;
    const top = arr.slice(0, 4);
    const otros = arr.slice(4).reduce((t, x) => t + x.horas, 0);
    return [...top, { nombre: 'Otros', horas: Math.round(otros * 100) / 100 }];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [misHoras.horas]);

  const totalHorasNum = parseFloat(misHoras.totalHoras || 0);

  return (
    <div className="mis-horas-container">
      <NavBarHome
        user={user}
        handleLogout={handleLogout}
      />
      
      <div className="mis-horas-content">
        <div className="mis-horas-header">
          <h1>Mis horas</h1>
          <p>Consultá y analizá tus horas registradas por cliente y período.</p>
        </div>

        <div className="mis-horas-filtros-header">
          <div className="mis-horas-filtros-container">
            {/* Filtro Por Cliente */}
            <div className="mis-horas-filtro-item">
              <label className="mis-horas-filtro-label">
                Filtrar por cliente
              </label>
              <div className="mis-horas-filtro-cliente">
                <Select
                  value={clienteSeleccionado}
                  onChange={setClienteSeleccionado}
                  options={opcionesClientes}
                  placeholder="Seleccionar cliente"
                  styles={customSelectStyles}
                  className="mis-horas-select-cliente"
                  classNamePrefix="rep-select"
                  isClearable={true}
                />
                
                <div className="mis-horas-select-tipo-container">
                 
                  <select 
                    value={tipoFiltroCliente} 
                    onChange={(e) => setTipoFiltroCliente(e.target.value)}
                    className="mis-horas-select-tipo-filtro"
                  >
                    <option value="todas">Todas las fechas</option>
                    <option value="mes-año">Por mes y año</option>
                    <option value="rango-fechas">Rango de fechas</option>
                  </select>
                </div>

                {/* Campos condicionales según el tipo de filtro */}
                {tipoFiltroCliente === 'mes-año' && (
                  <>
                    <div className="mis-horas-input-container">
                      <label className="mis-horas-input-label">Mes:</label>
                      <input
                        type="text"
                        placeholder="Mes (01-12)"
                        value={mes}
                        onChange={(e) => setMes(e.target.value)}
                        maxLength="2"
                        className="mis-horas-input-mes"
                      />
                    </div>
                    <div className="mis-horas-input-container">
                      <label className="mis-horas-input-label">Año:</label>
                      <input
                        type="text"
                        placeholder="Año (2024)"
                        value={año}
                        onChange={(e) => setAño(e.target.value)}
                        maxLength="4"
                        className="mis-horas-input-año"
                      />
                    </div>
                  </>
                )}

                {tipoFiltroCliente === 'rango-fechas' && (
                  <>
                    <div className="mis-horas-input-container">
                      <label className="mis-horas-input-label">Inicio:</label>
                      <DatePicker
                        selected={clienteFechaInicio}
                        onChange={(date) => setClienteFechaInicio(date)}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Fecha inicio"
                        className="mis-horas-date-picker-input"
                        showPopperArrow={false}
                        autoComplete="off"
                        maxDate={new Date()}
                      />
                    </div>
                    <div className="mis-horas-input-container">
                      <label className="mis-horas-input-label">Fin:</label>
                      <DatePicker
                        selected={clienteFechaFin}
                        onChange={(date) => setClienteFechaFin(date)}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Fecha fin"
                        className="mis-horas-date-picker-input"
                        showPopperArrow={false}
                        autoComplete="off"
                        maxDate={new Date()}
                      />
                    </div>
                  </>
                )}

                <button 
                  className="mis-horas-filtro-btn"
                  onClick={handleFiltroPorCliente}
                >
                  Buscar 
                </button>
                <button 
                  className="mis-horas-filtro-btn mis-horas-filtro-btn-limpiar"
                  onClick={limpiarFiltros}
                >
                  Limpiar filtro
                </button>
              </div>
            </div>

            {/* Filtro Por Semana */}
            <div className="mis-horas-filtro-item">
              <label className="mis-horas-filtro-label">
                Filtrar por semana (todos los clientes)
              </label>
              <div className="mis-horas-filtro-semana">
                <div className="mis-horas-input-container">
                  <label className="mis-horas-input-label">Inicio:</label>
                  <DatePicker
                    selected={fechaInicio}
                    onChange={(date) => setFechaInicio(date)}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Fecha inicio"
                    className="mis-horas-date-picker-input"
                    showPopperArrow={false}
                    autoComplete="off"
                    maxDate={new Date()}
                  />
                </div>
                <div className="mis-horas-input-container">
                  <label className="mis-horas-input-label"> Fin:</label>
                  <DatePicker
                    selected={fechaFin}
                    onChange={(date) => setFechaFin(date)}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Fecha fin"
                    className="mis-horas-date-picker-input"
                    showPopperArrow={false}
                    autoComplete="off"
                    maxDate={new Date()}
                  />
                </div>
                <button 
                  className="mis-horas-filtro-btn"
                  onClick={handleFiltroPorSemana}
                >
                  Buscar
                </button>
                <button 
                  className="mis-horas-filtro-btn mis-horas-filtro-btn-limpiar"
                  onClick={limpiarFiltros}
                >
                  Limpiar filtro
                </button>
              </div>
            </div>

            {/* Filtro Por Fecha Específica */}
            <div className="mis-horas-filtro-item">
              <label className="mis-horas-filtro-label">
                Filtrar por fecha específica (todos los clientes)
              </label>
              <div className="mis-horas-filtro-fecha">
                <div className="mis-horas-input-container">
                  <label className="mis-horas-input-label">Fecha:</label>
                  <DatePicker
                    selected={fechaEspecifica}
                    onChange={(date) => setFechaEspecifica(date)}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Fecha"
                    className="mis-horas-date-picker-input"
                    showPopperArrow={false}
                    autoComplete="off"
                    maxDate={new Date()}
                  />
                </div>
                <button 
                  className="mis-horas-filtro-btn"
                  onClick={handleFiltroPorFecha}
                >
                  Buscar
                </button>
                <button 
                  className="mis-horas-filtro-btn mis-horas-filtro-btn-limpiar"
                  onClick={limpiarFiltros}
                >
                  Limpiar filtro
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mostrar loading */}
        {misHoras.loading && <LoadingSpinner />}

        {/* Mostrar error */}
        {misHoras.error && (
          <div className="error-message">
            Error: {misHoras.error}
          </div>
        )}

        {/* Mostrar resumen */}
        {!misHoras.loading && !misHoras.error && (
          <div className="resumen-horas">
            <div className="resumen-header">
              <h3>Resumen</h3>
            </div>

            <div className="resumen-stats">
              <div className="stat-item">
                <span className="stat-label">Total de Horas:</span>
                <span className="stat-value">{formatearHoras(misHoras.totalHoras || 0)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Total de Registros:</span>
                <span className="stat-value">{misHoras.horas?.length || 0}</span>
              </div>
              {misHoras.filtrosActivos?.totalDias && (
                <div className="stat-item">
                  <span className="stat-label">Días con Registro:</span>
                  <span className="stat-value">{misHoras.filtrosActivos.totalDias}</span>
                </div>
              )}
              
              {/* Mostrar cliente seleccionado si filtré por cliente */}
              {clienteFiltrado && (
                <div className="stat-item cliente-filtrado">
                  <span className="stat-label">Cliente Filtrado:</span>
                  <span className="stat-value cliente-nombre">{clienteFiltrado.label}</span>
                </div>
              )}
            </div>

            {/* Gráficos */}
            {horasArr.length > 0 && (
              <div className="rep-charts">
                <div className="rep-card">
                  <p className="rep-chart-title">Horas por día</p>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={horasPorDia} margin={{ top: 24, right: 8, left: -8, bottom: 4 }} barCategoryGap="25%">
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                      <XAxis dataKey="etiqueta" interval={0} tick={{ fill: '#8A988F', fontSize: 11 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} tickLine={false} angle={-35} textAnchor="end" height={50} />
                      <YAxis tick={{ fill: '#8A988F', fontSize: 12 }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip cursor={{ fill: 'rgba(40,199,111,0.08)' }} contentStyle={tooltipStyle} itemStyle={{ color: '#E8EFEA' }} labelStyle={{ color: '#9fb0a6' }} formatter={(v) => [`${v} h`, 'Horas']} />
                      <Bar dataKey="horas" fill="#28C76F" radius={[6, 6, 0, 0]} maxBarSize={48}>
                        <LabelList dataKey="horas" position="top" fill="#3FE07F" fontSize={11} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="rep-card">
                  <p className="rep-chart-title">Distribución por cliente</p>
                  <div className="rep-donut-row">
                    <div className="rep-donut-chart">
                      <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                          <Pie data={donutClientes} dataKey="horas" nameKey="nombre" cx="50%" cy="50%" innerRadius={52} outerRadius={80} paddingAngle={2} stroke="none">
                            {donutClientes.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: '#E8EFEA' }} labelStyle={{ color: '#9fb0a6' }} formatter={(v, n) => [`${v} h`, n]} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="rep-legend">
                      {donutClientes.map((e, i) => (
                        <div className="rep-legend-li" key={i}>
                          <span className="rep-legend-dot" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }}></span>
                          <span className="rep-legend-name">{e.nombre}</span>
                          <span className="rep-legend-pct">{totalHorasNum ? Math.round((e.horas / totalHorasNum) * 100) : 0}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tabla de resumen de clientes - mostrar siempre que haya datos */}
            {obtenerResumenClientes().length > 0 && (
              <div className="resumen-clientes">
                <div className="resumen-clientes-header">
                  <div className="resumen-titulo-container">
                    <h4>Resumen por cliente</h4>
                    <span className="filtro-descripcion">{obtenerDescripcionFiltro()}</span>
                  </div>
                  <button
                    className="filtro-btn btn-exportar btn-exportar-small"
                    onClick={exportarResumenClientesAExcel}
                  >
                    Descargar resumen
                  </button>
                </div>
                <div className="tabla-clientes-container">
                  <table className="tabla-clientes">
                    <thead>
                      <tr>
                        <th>Cliente</th>
                        <th>Horas Totales</th>
                        <th>% del Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {obtenerResumenClientes().map((cliente, index) => (
                        <tr key={index}>
                          <td className="cliente-nombre">{cliente.nombre}</td>
                          <td className="horas-total mis-horas-horas-cell">{formatearHoras(cliente.horas)}</td>
                          <td className="porcentaje">
                            {((cliente.horas / (misHoras.totalHoras || 1)) * 100).toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td></td>
                        <td className="horas-total">{formatearHoras(misHoras.totalHoras || 0)}</td>
                        <td>100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tabla de horas */}
        {!misHoras.loading && !misHoras.error && misHoras.horas && misHoras.horas.length > 0 && (
          <div className="tabla-horas-container">
            <div className="tabla-header">
              <h3>Registros de horas</h3>
              {/* Botón de descarga */}
              <button
                className="filtro-btn btn-exportar"
                onClick={exportarAExcel}
                disabled={!misHoras.horas || misHoras.horas.length === 0}
              >
                Descargar Excel
              </button>
            </div>
            <table className="tabla-horas">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Cliente</th>
                  <th>Servicio</th>
                  <th>Tarea</th>
                  <th>Horas</th>
                  <th>Descripción</th>
                </tr>
              </thead>
              <tbody>
                {misHoras.horas.map((hora, index) => (
                  <tr key={index}>
                    <td>{formatearFecha(hora.fechaCarga)}</td>
                    <td>{hora.cliente?.nombre || 'Sin cliente'}</td>
                    <td>{hora.servicio?.nombre || 'Sin servicio'}</td>
                    <td>{hora.tarea?.nombre || 'Sin tarea'}</td>
                    <td className="horas-cell mis-horas-horas-cell">{formatearHoras(hora.horas)}</td>
                    <td className="descripcion-cell">{hora.descripcion}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td className="horas-cell">{formatearHoras(misHoras.totalHoras || 0)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Mensaje cuando no hay datos */}
        {!misHoras.loading && !misHoras.error && (!misHoras.horas || misHoras.horas.length === 0) && (
          <div className="no-data-message">
            <p>
              {filtroActivo === 'ninguno' 
                ? 'Selecciona un filtro para ver tus horas registradas.' 
                : 'No se encontraron registros de horas para los filtros seleccionados.'
              }
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

export default MisHoras;